import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "../../components/CartContext";
import "./css/CartScreen.css";

function CartScreen() {
  const navigate = useNavigate();
  const { restaurantId, tableId } = useParams();

  const {
    cart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    cartTotal,
    cartCount,
  } = useCart();

  const handleBack = () => {
    navigate(`/${restaurantId}/table/${tableId}`);
  };

  const handleCheckout = () => {
    if (!cart.length) return;

    navigate(
      `/${restaurantId}/table/${tableId}/checkout`
    );
  };

  return (
    <div className="theme3-cart-page">

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <section className="theme3-cart-page-header">

        <div className="theme3-cart-container">

          <button
            className="theme3-cart-back"
            onClick={handleBack}
          >
            <span>←</span>
            BACK TO MENU
          </button>

          <div className="theme3-cart-title">

            <span className="theme3-cart-eyebrow">
              YOUR SELECTION
            </span>

            <h1>
              Your <em>Order</em>
            </h1>

            <div className="theme3-cart-title-line">
              <span></span>
              <b>✦</b>
              <span></span>
            </div>

            <p>
              Review your selection before continuing.
            </p>

          </div>

        </div>

      </section>


      {/* =====================================================
          CART CONTENT
      ===================================================== */}

      <section className="theme3-cart-content">

        <div className="theme3-cart-container">

          {cart.length === 0 ? (

            /* =================================================
               EMPTY CART
            ================================================= */

            <div className="theme3-cart-empty-page">

              <div className="theme3-cart-empty-icon">
                🛒
              </div>

              <span className="theme3-cart-empty-label">
                YOUR CART IS EMPTY
              </span>

              <h2>
                Nothing selected <em>yet.</em>
              </h2>

              <p>
                Explore our menu and discover something
                delicious to enjoy.
              </p>

              <button
                className="theme3-cart-menu-button"
                onClick={handleBack}
              >
                EXPLORE MENU
                <span>→</span>
              </button>

            </div>

          ) : (

            <div className="theme3-cart-layout">

              {/* =============================================
                  LEFT - ITEMS
              ============================================= */}

              <div className="theme3-cart-items-section">

                <div className="theme3-cart-items-heading">

                  <div>
                    <span>01</span>

                    <h2>
                      Selected <em>Dishes</em>
                    </h2>
                  </div>

                  <strong>
                    {cartCount}{" "}
                    {cartCount === 1 ? "ITEM" : "ITEMS"}
                  </strong>

                </div>


                <div className="theme3-cart-items-list">

                  {cart.map((item) => {

                    const itemTotal =
                      Number(item.price || 0) *
                      Number(item.quantity || 0);

                    return (
                      <div
                        className="theme3-cart-product"
                        key={item.id}
                      >

                        {/* IMAGE */}

                        <div className="theme3-cart-product-image">

                          <img
                            src={
                              item.image ||
                              "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=500&q=80"
                            }
                            alt={item.name}
                          />

                        </div>


                        {/* DETAILS */}

                        <div className="theme3-cart-product-details">

                          <div className="theme3-cart-product-top">

                            <div>

                              <span className="theme3-cart-product-category">
                                {item.category_name ||
                                  item.category ||
                                  "SPICE FUSION"}
                              </span>

                              <h3>
                                {item.name}
                              </h3>

                            </div>

                            <strong className="theme3-cart-product-price">
                              ₹
                              {itemTotal.toFixed(2)}
                            </strong>

                          </div>


                          {item.description && (
                            <p className="theme3-cart-product-description">
                              {item.description}
                            </p>
                          )}


                          <div className="theme3-cart-product-bottom">

                            {/* QUANTITY */}

                            <div className="theme3-cart-quantity">

                              <button
                                type="button"
                                onClick={() =>
                                  decreaseQuantity(item.id)
                                }
                                aria-label={`Decrease ${item.name}`}
                              >
                                −
                              </button>

                              <strong>
                                {item.quantity}
                              </strong>

                              <button
                                type="button"
                                onClick={() =>
                                  increaseQuantity(item.id)
                                }
                                aria-label={`Increase ${item.name}`}
                              >
                                +
                              </button>

                            </div>


                            {/* UNIT PRICE */}

                            <span className="theme3-cart-unit-price">
                              ₹
                              {Number(
                                item.price || 0
                              ).toFixed(2)}{" "}
                              each
                            </span>


                            {/* REMOVE */}

                            <button
                              type="button"
                              className="theme3-cart-remove"
                              onClick={() =>
                                removeFromCart(item.id)
                              }
                            >
                              REMOVE
                            </button>

                          </div>

                        </div>

                      </div>
                    );
                  })}

                </div>


                {/* CONTINUE SHOPPING */}

                <button
                  className="theme3-cart-continue"
                  onClick={handleBack}
                >
                  <span>←</span>
                  CONTINUE EXPLORING THE MENU
                </button>

              </div>


              {/* =============================================
                  RIGHT - SUMMARY
              ============================================= */}

              <aside className="theme3-cart-summary">

                <div className="theme3-cart-summary-inner">

                  <span className="theme3-summary-eyebrow">
                    YOUR ORDER
                  </span>

                  <h2>
                    Order <em>Summary</em>
                  </h2>


                  <div className="theme3-summary-line">
                    <span>ITEMS</span>

                    <strong>
                      {cartCount}
                    </strong>
                  </div>


                  <div className="theme3-summary-line">
                    <span>SUBTOTAL</span>

                    <strong>
                      ₹
                      {Number(
                        cartTotal || 0
                      ).toFixed(2)}
                    </strong>
                  </div>


                  <div className="theme3-summary-divider">
                  </div>


                  <div className="theme3-summary-total">

                    <span>
                      TOTAL
                    </span>

                    <strong>
                      ₹
                      {Number(
                        cartTotal || 0
                      ).toFixed(2)}
                    </strong>

                  </div>


                  <p className="theme3-summary-note">
                    Taxes and applicable charges will
                    be calculated at checkout.
                  </p>


                  <button
                    className="theme3-checkout-button"
                    onClick={handleCheckout}
                  >
                    CONTINUE TO CHECKOUT
                    <span>→</span>
                  </button>


                  <div className="theme3-summary-footer">

                    <span>✦</span>

                    <p>
                      Freshly prepared with care
                    </p>

                    <span>✦</span>

                  </div>

                </div>

              </aside>

            </div>

          )}

        </div>

      </section>

    </div>
  );
}

export default CartScreen;